/**
 * ⚙️ AffiliateEinstellungenPage - Profil & Einstellungen
 */

import React, { useState } from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  CreditCard, 
  Bell, 
  Shield,
  Save,
  AlertCircle,
  HelpCircle,
  MessageSquare,
  Phone,
  Mail,
  FileText,
  ExternalLink
} from 'lucide-react';

// ============================================
// MOCK DATA
// ============================================

const MOCK_PROFILE = {
  firstName: 'Martin',
  lastName: 'Kowalski',
  email: 'martin.kowalski@example.com',
  phone: '+49 172 1234567',
  company: 'MK Solutions GmbH',
  vatId: 'DE123456789',
  street: 'Musterstraße 123',
  city: 'Berlin',
  zip: '10115',
  country: 'Deutschland',
  referralCode: 'martink',
};

const MOCK_PAYMENT = {
  method: 'sepa',
  iban: 'DE89370400440532013000',
  bic: 'COBADEFFXXX',
  accountHolder: 'Martin Kowalski',
  paypalEmail: '',
  minPayout: '50',
};

const MOCK_NOTIFICATIONS = {
  emailNewLead: true,
  emailConversion: true,
  emailPayout: true,
  emailNewsletter: false,
  pushEnabled: true,
  pushNewLead: true,
  pushConversion: true,
};

// ============================================
// COMPONENTS
// ============================================

const ProfileSection: React.FC<{ onSave?: () => void }> = ({ onSave }) => {
  const [profile, setProfile] = useState(MOCK_PROFILE);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <User className="w-5 h-5" />
          Persönliche Daten
        </CardTitle>
        <CardDescription>Deine Kontaktdaten für die Kommunikation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">Vorname</Label>
            <Input 
              id="firstName" 
              value={profile.firstName} 
              onChange={(e) => setProfile({...profile, firstName: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Nachname</Label>
            <Input 
              id="lastName" 
              value={profile.lastName}
              onChange={(e) => setProfile({...profile, lastName: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" value={profile.email} disabled />
            <p className="text-xs text-gray-500">E-Mail kann nicht geändert werden</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefon</Label>
            <Input 
              id="phone" 
              value={profile.phone}
              onChange={(e) => setProfile({...profile, phone: e.target.value})}
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="company">Firma (optional)</Label>
            <Input 
              id="company" 
              value={profile.company}
              onChange={(e) => setProfile({...profile, company: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vatId">USt-IdNr. (optional)</Label>
            <Input 
              id="vatId" 
              value={profile.vatId}
              onChange={(e) => setProfile({...profile, vatId: e.target.value})}
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="street">Straße & Hausnummer</Label>
            <Input 
              id="street" 
              value={profile.street}
              onChange={(e) => setProfile({...profile, street: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zip">PLZ</Label>
            <Input 
              id="zip" 
              value={profile.zip}
              onChange={(e) => setProfile({...profile, zip: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">Stadt</Label>
            <Input 
              id="city" 
              value={profile.city}
              onChange={(e) => setProfile({...profile, city: e.target.value})}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="country">Land</Label>
            <Select value={profile.country} onValueChange={(v) => setProfile({...profile, country: v})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Deutschland">Deutschland</SelectItem>
                <SelectItem value="Österreich">Österreich</SelectItem>
                <SelectItem value="Schweiz">Schweiz</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button className="bg-primary hover:bg-primary/90" onClick={onSave}>
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const PaymentSection: React.FC<{ onSave?: () => void }> = ({ onSave }) => {
  const [payment, setPayment] = useState(MOCK_PAYMENT);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Zahlungsinformationen
        </CardTitle>
        <CardDescription>Gib an, wie du deine Provisionen erhalten möchtest</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Auszahlungsmethode</Label>
          <Select value={payment.method} onValueChange={(v) => setPayment({...payment, method: v})}>
            <SelectTrigger className="w-full md:w-[300px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sepa">SEPA Überweisung</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {payment.method === 'sepa' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="iban">IBAN</Label>
              <Input 
                id="iban" 
                value={payment.iban}
                onChange={(e) => setPayment({...payment, iban: e.target.value})}
                placeholder="DE00 0000 0000 0000 0000 00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bic">BIC</Label>
              <Input 
                id="bic" 
                value={payment.bic}
                onChange={(e) => setPayment({...payment, bic: e.target.value})}
                placeholder="COBADEFFXXX"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="accountHolder">Kontoinhaber</Label>
              <Input 
                id="accountHolder" 
                value={payment.accountHolder}
                onChange={(e) => setPayment({...payment, accountHolder: e.target.value})}
              />
            </div>
          </div>
        )}

        {payment.method === 'paypal' && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="paypalEmail">PayPal E-Mail</Label>
              <Input 
                id="paypalEmail" 
                type="email"
                value={payment.paypalEmail}
                onChange={(e) => setPayment({...payment, paypalEmail: e.target.value})}
                placeholder="deine@email.de"
              />
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Mindestauszahlungsbetrag</Label>
          <Select value={payment.minPayout} onValueChange={(v) => setPayment({...payment, minPayout: v})}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">€50</SelectItem>
              <SelectItem value="100">€100</SelectItem>
              <SelectItem value="250">€250</SelectItem>
              <SelectItem value="500">€500</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">Auszahlung erfolgt erst ab diesem Betrag</p>
        </div>

        <div className="flex justify-end">
          <Button className="bg-primary hover:bg-primary/90" onClick={onSave}>
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const NotificationSection: React.FC<{ onSave?: () => void }> = ({ onSave }) => {
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

  const toggleNotification = (key: keyof typeof MOCK_NOTIFICATIONS) => {
    setNotifications({...notifications, [key]: !notifications[key]});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Benachrichtigungen
        </CardTitle>
        <CardDescription>Wähle aus, wie du informiert werden möchtest</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Notifications */}
        <div>
          <h4 className="text-sm font-medium mb-4">E-Mail Benachrichtigungen</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Neuer Lead</p>
                <p className="text-xs text-gray-500">Bei jedem neuen Lead eine E-Mail erhalten</p>
              </div>
              <Switch 
                checked={notifications.emailNewLead}
                onCheckedChange={() => toggleNotification('emailNewLead')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Conversion</p>
                <p className="text-xs text-gray-500">Bei erfolgreicher Konvertierung benachrichtigt werden</p>
              </div>
              <Switch 
                checked={notifications.emailConversion}
                onCheckedChange={() => toggleNotification('emailConversion')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Auszahlung</p>
                <p className="text-xs text-gray-500">Bei erfolgter Auszahlung</p>
              </div>
              <Switch 
                checked={notifications.emailPayout}
                onCheckedChange={() => toggleNotification('emailPayout')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Newsletter</p>
                <p className="text-xs text-gray-500">Updates und Tipps für Affiliates</p>
              </div>
              <Switch 
                checked={notifications.emailNewsletter}
                onCheckedChange={() => toggleNotification('emailNewsletter')}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Push Notifications */}
        <div>
          <h4 className="text-sm font-medium mb-4">Push-Benachrichtigungen</h4>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Push aktivieren</p>
                <p className="text-xs text-gray-500">Browser-Benachrichtigungen erlauben</p>
              </div>
              <Switch 
                checked={notifications.pushEnabled}
                onCheckedChange={() => toggleNotification('pushEnabled')}
              />
            </div>
            {notifications.pushEnabled && (
              <>
                <div className="flex items-center justify-between ml-4">
                  <p className="text-sm">Neuer Lead</p>
                  <Switch 
                    checked={notifications.pushNewLead}
                    onCheckedChange={() => toggleNotification('pushNewLead')}
                  />
                </div>
                <div className="flex items-center justify-between ml-4">
                  <p className="text-sm">Conversion</p>
                  <Switch 
                    checked={notifications.pushConversion}
                    onCheckedChange={() => toggleNotification('pushConversion')}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button className="bg-primary hover:bg-primary/90" onClick={onSave}>
            <Save className="w-4 h-4 mr-2" />
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SecuritySection: React.FC = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Sicherheit
        </CardTitle>
        <CardDescription>Schütze deinen Account</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Passwort ändern</p>
              <p className="text-xs text-gray-500 mb-3">Wir empfehlen ein starkes, einzigartiges Passwort</p>
              <Button variant="outline" size="sm">
                Passwort ändern
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Zwei-Faktor-Authentifizierung</p>
              <p className="text-xs text-gray-500 mb-3">Erhöhe die Sicherheit deines Accounts</p>
              <Button variant="outline" size="sm">
                2FA aktivieren
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Account löschen</p>
              <p className="text-xs text-red-600 mb-3">
                Diese Aktion kann nicht rückgängig gemacht werden. 
                Alle Daten werden unwiderruflich gelöscht.
              </p>
              <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">
                Account löschen
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SupportSection: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Partner Manager Card */}
      <Card className="bg-gradient-to-r from-emerald-50/80 to-emerald-100/60 border-emerald-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              MK
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Dein Partner Manager</h3>
              <p className="text-emerald-700 font-medium">Markus Keller</p>
              <p className="text-sm text-gray-600 mt-1">Ich bin persönlich für dich da und unterstütze dich beim Wachstum deines Portfolios.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Button variant="outline" size="sm" className="border-emerald-300 hover:bg-emerald-50">
                  <Mail className="w-4 h-4 mr-2" />
                  E-Mail senden
                </Button>
                <Button variant="outline" size="sm" className="border-emerald-300 hover:bg-emerald-50">
                  <Phone className="w-4 h-4 mr-2" />
                  +49 30 123456
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Support Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Support & Hilfe
          </CardTitle>
          <CardDescription>Finde Antworten oder kontaktiere unser Team</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* FAQ */}
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">FAQ / Wissensdatenbank</p>
                <p className="text-sm text-gray-500">Häufig gestellte Fragen und Anleitungen</p>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
            </div>
          </div>

          {/* Support Ticket */}
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 group-hover:text-emerald-600 transition-colors">Support Ticket erstellen</p>
                <p className="text-sm text-gray-500">Sende eine Anfrage an unser Support-Team</p>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                Neues Ticket
              </Button>
            </div>
          </div>

          {/* Live Chat */}
          <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group">
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-purple-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">Live Chat</p>
                <p className="text-sm text-gray-500">Chatte direkt mit unserem Team (Mo-Fr 9-18 Uhr)</p>
              </div>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">Online</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Schnellzugriff</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Button variant="outline" className="h-auto py-4 flex-col gap-2">
              <FileText className="w-5 h-5" />
              <span className="text-xs">Dokumentation</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2">
              <MessageSquare className="w-5 h-5" />
              <span className="text-xs">Community</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2">
              <Mail className="w-5 h-5" />
              <span className="text-xs">Newsletter</span>
            </Button>
            <Button variant="outline" className="h-auto py-4 flex-col gap-2">
              <Phone className="w-5 h-5" />
              <span className="text-xs">Rückruf</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateEinstellungenPage: React.FC = () => {
  const { toast } = useToast();
  
  const handleSave = (section: string) => {
    toast({
      title: '✅ Gespeichert',
      description: `${section} wurden erfolgreich gespeichert.`,
    });
  };
  
  return (
    <AffiliateLayout
      pageTitle="Einstellungen"
      pageSubtitle="Verwalte dein Profil und deine Präferenzen"
    >
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="bg-gray-100 flex-wrap h-auto p-1">
          <TabsTrigger value="profile" className="text-xs sm:text-sm">
            <User className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Profil</span>
          </TabsTrigger>
          <TabsTrigger value="payment" className="text-xs sm:text-sm">
            <CreditCard className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Zahlung</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs sm:text-sm">
            <Bell className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Benachrichtigungen</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs sm:text-sm">
            <Shield className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Sicherheit</span>
          </TabsTrigger>
          <TabsTrigger value="support" className="text-xs sm:text-sm">
            <HelpCircle className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden xs:inline">Support</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfileSection onSave={() => handleSave('Profildaten')} />
        </TabsContent>

        <TabsContent value="payment">
          <PaymentSection onSave={() => handleSave('Zahlungsdaten')} />
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationSection onSave={() => handleSave('Benachrichtigungen')} />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySection />
        </TabsContent>

        <TabsContent value="support">
          <SupportSection />
        </TabsContent>
      </Tabs>
    </AffiliateLayout>
  );
};

export default AffiliateEinstellungenPage;
